import React, { useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import TrustedBy from '../components/TrustedBy';
import About from '../components/About';
import Services from '../components/Services';
import LogoTicker from '../components/LogoTicker';
import Packages from '../components/Packages';
import Work from '../components/Work';
import Contact from '../components/Contact';
import FAQ from '../components/FAQ';
import Footer from '../components/Footer';
import VideoModal from '../components/VideoModal';
import VideoPreviewSection from '../components/VideoPreviewSection';
import FeatureVideoSection from '../components/FeatureVideoSection';
import Seo from '../components/Seo';
import { documentarySections, kelownaLoveLetter, tradesPage, proSports, docBlurb, proSportsBlurb } from '../mock';

export default function Home() {
  const [video, setVideo] = useState(null);
  const openVideo = useCallback((youtubeId, title) => setVideo({ youtubeId, title }), []);
  const closeVideo = useCallback(() => setVideo(null), []);

  const docFeatured = documentarySections
    .filter((section) => section.id !== 'stories-from-okanagan')
    .flatMap((section) => section.videos)
    .slice(0, 3);
  const proSportsFeatured = proSports.videos.slice(0, 3);

  return (
    <>
      <Seo
        title="Simply Gold Productions | Documentary & Video Production, Kelowna"
        description="Documentary storytelling for companies doing extraordinary work. Commercial, sports, documentary, drone & trades films that build trust and grow brands across Canada."
        path="/"
        type="website"
      />
      <Navbar />
      <main>
        <Hero onPlay={openVideo} />
        <About onPlay={openVideo} />
        <FeatureVideoSection
          id="our-home"
          eyebrow="Rooted in British Columbia"
          heading="A Love Letter to Kelowna"
          blurb="With teams based in the Okanagan and Vancouver, we tell stories across British Columbia and beyond. This film is our thank-you to Kelowna—one of the communities that has welcomed us, trusted us and helped shape Simply Gold Productions."
          video={kelownaLoveLetter}
          viewAllTo="/documentaries"
          viewAllLabel="Explore Stories"
          onPlay={openVideo}
        />
        <TrustedBy />
        <Services onPlay={openVideo} />
        <Work onPlay={openVideo} />
        <section className="max-w-4xl mx-auto px-6 py-20 md:py-28 text-center" data-testid="trust-statement">
          <p className="font-display font-bold text-white text-3xl md:text-5xl leading-[1.12] uppercase">
            The right film helps customers trust you{' '}
            <span className="text-[#c9a85f]">before you've ever met them.</span>
          </p>
        </section>
        <FeatureVideoSection
          id="trades"
          eyebrow="For Trades & Construction"
          heading="Our Process"
          blurb="See exactly how we work — from the first conversation to the final cut. We embed with your crew on real job sites and turn everyday work into video that builds trust and wins bigger contracts."
          video={tradesPage.process}
          viewAllTo="/trades"
          viewAllLabel="View All Trades Work"
          onPlay={openVideo}
        />
        <LogoTicker
          category="Trades"
          names={[
            'Core Mechanical',
            'Whyte Mechanical',
            'JDG Interior Design +',
            'Limitless Audio Visual Integration',
            'The Gas Guys Outdoor Designs',
            'Enviroware Projects',
            'Eviston Real Estate',
            'Greenpark Landscaping',
            'Synscape',
          ]}
          heading="Trusted By Trades & Construction Teams"
          gold
        />
        <VideoPreviewSection
          id="pro-sports"
          eyebrow="Pro Sports"
          heading="Pro Sports"
          blurb={proSportsBlurb}
          videos={proSportsFeatured}
          viewAllTo="/pro-sports"
          viewAllLabel="View All Pro Sports"
          onPlay={openVideo}
        />
        <VideoPreviewSection
          id="documentaries"
          eyebrow="Documentary & Storytelling"
          heading="Documentaries"
          blurb={docBlurb}
          videos={docFeatured}
          viewAllTo="/documentaries"
          viewAllLabel="View All Documentaries"
          onPlay={openVideo}
        />
        <Packages />
        <Contact />
        <FAQ />
      </main>
      <Footer />
      {video && (
        <VideoModal youtubeId={video.youtubeId} title={video.title} onClose={closeVideo} />
      )}
    </>
  );
}
